import React, { useState } from 'react';
import {
  Container,
  Card,
  Stack,
  Typography,
  Tabs,
  Tab,
  Box,
  Breadcrumbs,
  Link,
  Chip,
  Button,
  Alert,
  Snackbar
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Iconify from '../../components/minimal/iconify';
import { TimeSlotSettingsSection } from '../dashboard/settings/TimeSlotSettingsSection';
import { EventBreadcrumbs } from '../common/EventBreadcrumbs';

// Mock Event Data with Time Slots
const mockEvent = {
  _id: 'event123',
  name: 'Basketball Training Session',
  handler: 'basketball-training',
  location: 'Sports Center, Tel Aviv',
  start_date: new Date('2025-09-01'),
  end_date: new Date('2025-12-31'),
  description: 'Professional basketball training with experienced coaches',
  design: {
    image: 'https://via.placeholder.com/300x200',
    background: 'https://via.placeholder.com/1200x400'
  },
  timeSlotConfig: {
    enabled: false,
    defaultSlotDuration: 30,
    defaultCapacity: 10,
    daySchedules: {
      monday: {
        enabled: true,
        startTime: "0900",
        endTime: "1700",
        slotDuration: 30,
        slotCapacity: 10,
        breakTimes: [
          { startTime: "1200", endTime: "1300", label: "Lunch Break" }
        ]
      },
      tuesday: {
        enabled: true,
        startTime: "0900",
        endTime: "1700",
        slotDuration: 30,
        slotCapacity: 10,
        breakTimes: [
          { startTime: "1200", endTime: "1300", label: "Lunch Break" }
        ]
      },
      wednesday: {
        enabled: true,
        startTime: "0900",
        endTime: "1700",
        slotDuration: 30,
        slotCapacity: 10,
        breakTimes: [
          { startTime: "1200", endTime: "1300", label: "Lunch Break" }
        ]
      },
      thursday: {
        enabled: true,
        startTime: "0900",
        endTime: "1700",
        slotDuration: 30,
        slotCapacity: 10,
        breakTimes: [
          { startTime: "1200", endTime: "1300", label: "Lunch Break" }
        ]
      },
      friday: {
        enabled: true,
        startTime: "0900",
        endTime: "2000",
        slotDuration: 30,
        slotCapacity: 15,
        breakTimes: [
          { startTime: "1200", endTime: "1300", label: "Lunch Break" },
          { startTime: "1700", endTime: "1730", label: "Dinner Break" }
        ]
      },
      saturday: {
        enabled: true,
        startTime: "1000",
        endTime: "2200",
        slotDuration: 45,
        slotCapacity: 20,
        breakTimes: [
          { startTime: "1400", endTime: "1500", label: "Afternoon Break" }
        ]
      },
      sunday: {
        enabled: true,
        startTime: "1200",
        endTime: "1700",
        slotDuration: 60,
        slotCapacity: 8,
        breakTimes: []
      }
    },
    dateOverrides: [
      {
        date: "2025-12-25",
        schedule: null,
        reason: "Christmas Day"
      },
      {
        date: "2025-12-31",
        schedule: {
          enabled: true,
          startTime: "1000",
          endTime: "1600",
          slotDuration: 30,
          slotCapacity: 10
        },
        reason: "New Year's Eve - Early Closing"
      }
    ],
    bookingWindow: {
      advanceBookingDays: 30,
      cutoffMinutesBeforeSlot: 60
    }
  }
};

// Simple Mock Component
export default function TimeSlotsMockSimple() {
  const [currentEvent, setCurrentEvent] = useState(mockEvent);
  const [currentTab, setCurrentTab] = useState('time_slots');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleSubmit = async (eventDataToUpdate) => {
    try {
      setIsLoading(true);

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Update the event with new data
      const updatedEvent = {
        ...currentEvent,
        ...eventDataToUpdate
      };

      setCurrentEvent(updatedEvent);

      setSnackbar({
        open: true,
        message: 'Time slot configuration saved successfully!',
        severity: 'success'
      });

      console.log('💾 Saved Time Slot Configuration:', updatedEvent.timeSlotConfig);
    } catch (e) {
      console.error(e);
      setSnackbar({
        open: true,
        message: 'Error saving configuration',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const TABS = [
    { value: 'overview', label: 'Overview' },
    { value: 'time_slots', label: 'Time Slots Configuration' },
    { value: 'json', label: 'JSON View' },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Stack spacing={3}>
          {/* Breadcrumbs */}
          <Breadcrumbs>
            <Link color="inherit" href="#" onClick={(e) => e.preventDefault()}>
              Mock Dashboard
            </Link>
            <Link color="inherit" href="#" onClick={(e) => e.preventDefault()}>
              Events
            </Link>
            <Typography color="text.primary">Event Settings</Typography>
          </Breadcrumbs>

          {/* Event Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack>
              <Typography variant="h4">{currentEvent.name}</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  📍 {currentEvent.location}
                </Typography>
                <Chip
                  size="small"
                  label={currentEvent.timeSlotConfig?.enabled ? "Time Slots: ON" : "Time Slots: OFF"}
                  color={currentEvent.timeSlotConfig?.enabled ? "success" : "default"}
                  icon={<Iconify icon="eva:clock-outline" />}
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<Iconify icon="eva:play-circle-outline" />}
                onClick={() => setCurrentTab('time_slots')}
              >
                Configure Time Slots
              </Button>
            </Stack>
          </Stack>

          {/* Info Alert */}
          <Alert severity="info" icon={<Iconify icon="eva:info-outline" />}>
            <Stack>
              <Typography variant="subtitle2">🎯 Time Slots Mock Dashboard</Typography>
              <Typography variant="caption">
                This is a fully functional demo. You can configure different schedules for different days,
                add break times, manage special dates, and see a live preview of your configuration.
              </Typography>
            </Stack>
          </Alert>

          {/* Main Content */}
          <Card>
            <Tabs
              value={currentTab}
              onChange={(e, v) => setCurrentTab(v)}
              sx={{ px: 2, bgcolor: 'background.neutral' }}
            >
              {TABS.map(tab => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Overview Tab */}
              {currentTab === 'overview' && (
                <Stack spacing={3}>
                  <Typography variant="h6">Event Overview</Typography>
                  <Card sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Stack spacing={2}>
                      <Typography variant="body2">
                        <strong>Event Name:</strong> {currentEvent.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Location:</strong> {currentEvent.location}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Time Slots:</strong> {currentEvent.timeSlotConfig?.enabled ? 'Enabled' : 'Disabled'}
                      </Typography>
                      {currentEvent.timeSlotConfig?.enabled && (
                        <>
                          <Typography variant="body2">
                            <strong>Booking Window:</strong> {currentEvent.timeSlotConfig.bookingWindow.advanceBookingDays} days in advance
                          </Typography>
                          <Typography variant="body2">
                            <strong>Special Dates:</strong> {currentEvent.timeSlotConfig.dateOverrides?.length || 0} configured
                          </Typography>
                        </>
                      )}
                    </Stack>
                  </Card>

                  <Alert severity="success">
                    <Typography variant="subtitle2">Quick Start:</Typography>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      <li>Click on "Time Slots Configuration" tab</li>
                      <li>Toggle "Enable Time Slot Booking" switch</li>
                      <li>Configure different hours for weekdays, Friday, Saturday, Sunday</li>
                      <li>Add break times (lunch, dinner, etc.)</li>
                      <li>Add special dates for holidays</li>
                      <li>See live preview on the right panel</li>
                      <li>Click Save to apply changes</li>
                    </ol>
                  </Alert>
                </Stack>
              )}

              {/* Time Slots Configuration Tab */}
              {currentTab === 'time_slots' && (
                <TimeSlotSettingsSection
                  onSubmit={handleSubmit}
                  isLoading={isLoading}
                  currentEvent={currentEvent}
                />
              )}

              {/* JSON View Tab */}
              {currentTab === 'json' && (
                <Stack spacing={3}>
                  <Typography variant="h6">Current Configuration (JSON)</Typography>
                  <Alert severity="info">
                    This shows the actual data structure that will be saved to the database
                  </Alert>
                  <Card sx={{ p: 2, bgcolor: 'grey.900' }}>
                    <pre style={{
                      color: '#00ff00',
                      margin: 0,
                      fontSize: '12px',
                      overflow: 'auto',
                      maxHeight: '600px'
                    }}>
                      {JSON.stringify(currentEvent.timeSlotConfig, null, 2)}
                    </pre>
                  </Card>
                </Stack>
              )}
            </Box>
          </Card>
        </Stack>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
      </Container>
    </LocalizationProvider>
  );
}